import React, { useCallback, useEffect, useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import { Play, RotateCcw } from 'lucide-react';
import { buildSampleSql, presetQueries, sampleSchema } from '../data/transitSample';

type ResultSet = { columns: string[]; values: (string | number | null)[][] };
type Status = 'idle' | 'booting' | 'ready' | 'error';

const QUERY_TIMEOUT_MS = 5000;
const SEED_TIMEOUT_MS = 20000;
const MAX_ROWS = 200;

const workerUrl = () => `${process.env.PUBLIC_URL || ''}/sql/worker.sql-wasm.js`;

export const SqlPlayground: React.FC = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const inView = useInView(sectionRef, { once: true, margin: '300px' });

  const workerRef = useRef<Worker | null>(null);
  const pendingRef = useRef(
    new Map<number, { resolve: (r: ResultSet[]) => void; reject: (e: Error) => void; timer: number }>()
  );
  const idRef = useRef(0);

  const [status, setStatus] = useState<Status>('idle');
  const [bootError, setBootError] = useState('');
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [query, setQuery] = useState(presetQueries[0].sql);
  const [activePreset, setActivePreset] = useState(0);
  const [result, setResult] = useState<ResultSet | null>(null);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [elapsed, setElapsed] = useState<number | null>(null);
  const [running, setRunning] = useState(false);

  const killWorker = useCallback((reason: string) => {
    workerRef.current?.terminate();
    workerRef.current = null;
    pendingRef.current.forEach((p) => {
      window.clearTimeout(p.timer);
      p.reject(new Error(reason));
    });
    pendingRef.current.clear();
  }, []);

  const call = useCallback(
    (sql: string, timeoutMs: number) =>
      new Promise<ResultSet[]>((resolve, reject) => {
        const worker = workerRef.current;
        if (!worker) {
          reject(new Error('Database is not running.'));
          return;
        }
        const id = ++idRef.current;
        const timer = window.setTimeout(() => {
          killWorker(`Query ran longer than ${timeoutMs / 1000}s and was stopped.`);
        }, timeoutMs);
        pendingRef.current.set(id, { resolve, reject, timer });
        worker.postMessage({ id, action: 'exec', sql });
      }),
    [killWorker]
  );

  const boot = useCallback(async () => {
    killWorker('restarting');
    setStatus('booting');
    setBootError('');
    try {
      const worker = new Worker(workerUrl());
      worker.onmessage = (e: MessageEvent) => {
        const { id, results, error: err } = e.data || {};
        const p = pendingRef.current.get(id);
        if (!p) return;
        window.clearTimeout(p.timer);
        pendingRef.current.delete(id);
        if (err) p.reject(new Error(err));
        else p.resolve(results || []);
      };
      worker.onerror = () => {
        setBootError('Could not start the in-browser database.');
        setStatus('error');
      };
      workerRef.current = worker;

      await call(buildSampleSql(), SEED_TIMEOUT_MS);
      await call('PRAGMA query_only = ON;', QUERY_TIMEOUT_MS);
      const stats = await call(
        sampleSchema.map((t) => `SELECT '${t.table}' AS t, COUNT(*) AS n FROM ${t.table}`).join(' UNION ALL ') + ';',
        QUERY_TIMEOUT_MS
      );
      const next: Record<string, number> = {};
      (stats[0]?.values || []).forEach((row) => {
        next[String(row[0])] = Number(row[1]);
      });
      setCounts(next);
      setStatus('ready');
    } catch (e) {
      setBootError(e instanceof Error ? e.message : 'Could not start the in-browser database.');
      setStatus('error');
    }
  }, [call, killWorker]);

  // boot lazily, only once the section is near the viewport
  useEffect(() => {
    if (inView && status === 'idle') boot();
  }, [inView, status, boot]);

  useEffect(() => () => killWorker('unmount'), [killWorker]);

  const run = useCallback(async () => {
    if (status !== 'ready' || running || !query.trim()) return;
    setRunning(true);
    setError('');
    setMessage('');
    const t0 = performance.now();
    try {
      const sets = await call(query, QUERY_TIMEOUT_MS);
      setElapsed(performance.now() - t0);
      if (sets.length === 0) {
        setResult(null);
        setMessage('Statement executed. No rows returned.');
      } else {
        setResult(sets[sets.length - 1]);
      }
    } catch (e) {
      setElapsed(null);
      setResult(null);
      const msg = e instanceof Error ? e.message : String(e);
      setError(msg);
      if (!workerRef.current) {
        // the worker was stopped (timeout) — rebuild the database in the background
        boot();
      }
    } finally {
      setRunning(false);
    }
  }, [status, running, query, call, boot]);

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
      e.preventDefault();
      run();
    } else if (e.key === 'Tab') {
      e.preventDefault();
      const el = e.currentTarget;
      const { selectionStart: s, selectionEnd: en } = el;
      const next = query.slice(0, s) + '  ' + query.slice(en);
      setQuery(next);
      requestAnimationFrame(() => el.setSelectionRange(s + 2, s + 2));
    }
  };

  const shown = result ? result.values.slice(0, MAX_ROWS) : [];

  return (
    <section id="playground" className="py-24 px-6 bg-background" ref={sectionRef}>
      <div className="max-w-5xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="mb-8"
        >
          <p className="font-mono text-sm text-accent mb-2">$ sqlite3 sample.db</p>
          <h2 className="text-3xl sm:text-4xl font-bold text-text mb-3">run my sql</h2>
          <p className="text-muted max-w-2xl">
            A real SQLite database running in your browser. Pick a query or write your own. The data
            is a small <span className="text-text">synthetic, GTFS-shaped sample</span>, not the real PMPML feed,
            but the tables match the Pune Transit project.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-[1fr_260px] gap-5">
          <div className="min-w-0">
            {/* preset chips */}
            <div className="flex flex-wrap gap-2 mb-3 font-mono text-xs">
              {presetQueries.map((p, i) => (
                <button
                  key={p.label}
                  onClick={() => {
                    setQuery(p.sql);
                    setActivePreset(i);
                  }}
                  className={`px-3 py-1.5 rounded border transition-colors ${
                    activePreset === i && query === p.sql
                      ? 'border-accent text-accent bg-accent/10'
                      : 'border-border text-muted hover:text-text'
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>

            {/* editor */}
            <div className="rounded-lg border border-border bg-surface overflow-hidden">
              <div className="flex items-center justify-between gap-2 px-4 py-2 bg-raised border-b border-border">
                <div className="flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-full bg-danger/60" />
                  <span className="w-2.5 h-2.5 rounded-full bg-accent/60" />
                  <span className="w-2.5 h-2.5 rounded-full bg-accent2/60" />
                  <span className="ml-3 text-xs font-mono text-muted">query.sql</span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={boot}
                    disabled={status === 'booting'}
                    className="p-1.5 text-muted hover:text-accent transition-colors disabled:opacity-40"
                    title="Reset database"
                    aria-label="Reset database"
                  >
                    <RotateCcw size={14} />
                  </button>
                  <button
                    onClick={run}
                    disabled={status !== 'ready' || running}
                    className="flex items-center gap-1.5 px-3 py-1 rounded bg-accent text-background font-mono text-xs font-semibold hover:bg-accent/90 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <Play size={12} fill="currentColor" />
                    {running ? 'running…' : 'run'}
                  </button>
                </div>
              </div>
              <textarea
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={onKeyDown}
                spellCheck={false}
                autoCapitalize="off"
                autoCorrect="off"
                aria-label="SQL query"
                rows={Math.min(16, Math.max(6, query.split('\n').length + 1))}
                className="w-full resize-y bg-background/60 text-text caret-accent font-mono text-xs sm:text-sm leading-relaxed p-4 outline-none"
              />
              <div className="px-4 py-1.5 border-t border-border font-mono text-[11px] text-muted flex justify-between">
                <span>ctrl / ⌘ + enter to run</span>
                <span>
                  {status === 'booting' && 'booting sqlite (wasm)…'}
                  {status === 'ready' && 'connected'}
                  {status === 'idle' && 'waiting…'}
                  {status === 'error' && 'offline'}
                </span>
              </div>
            </div>

            {/* results */}
            <div className="mt-4 rounded-lg border border-border bg-surface overflow-hidden min-h-[120px]">
              <div className="px-4 py-2 bg-raised border-b border-border text-xs font-mono text-muted flex justify-between">
                <span>result</span>
                {result && elapsed !== null && (
                  <span>
                    {result.values.length} row{result.values.length === 1 ? '' : 's'} · {elapsed.toFixed(1)} ms
                  </span>
                )}
              </div>

              {status === 'error' && (
                <p className="p-4 font-mono text-sm text-danger">
                  {bootError || 'The in-browser database could not start.'}
                </p>
              )}
              {status === 'booting' && (
                <p className="p-4 font-mono text-sm text-muted">
                  building sample database
                  <span className="inline-block w-2 h-4 bg-accent ml-1 align-middle animate-blink" />
                </p>
              )}
              {error && <p className="p-4 font-mono text-sm text-danger whitespace-pre-wrap">{error}</p>}
              {message && <p className="p-4 font-mono text-sm text-accent2">{message}</p>}
              {status === 'ready' && !result && !error && !message && (
                <p className="p-4 font-mono text-sm text-muted">press run to see rows here.</p>
              )}

              {result && (
                <div className="overflow-auto max-h-[420px]">
                  <table className="w-full text-left font-mono text-xs sm:text-sm">
                    <thead className="sticky top-0 bg-surface">
                      <tr>
                        {result.columns.map((c) => (
                          <th key={c} className="px-4 py-2 text-accent2 font-medium border-b border-border whitespace-nowrap">
                            {c}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {shown.map((row, i) => (
                        <tr key={i} className="hover:bg-raised/60">
                          {row.map((cell, j) => (
                            <td
                              key={j}
                              className={`px-4 py-1.5 border-b border-border/50 whitespace-nowrap ${
                                typeof cell === 'number' ? 'text-right text-text' : 'text-text'
                              }`}
                            >
                              {cell === null ? <span className="text-muted">NULL</span> : String(cell)}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {result.values.length > MAX_ROWS && (
                    <p className="px-4 py-2 text-xs font-mono text-muted">
                      showing first {MAX_ROWS} of {result.values.length} rows
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* schema panel */}
          <aside className="rounded-lg border border-border bg-surface/60 p-4 h-fit">
            <p className="font-mono text-xs text-accent mb-3">.schema</p>
            <div className="space-y-4">
              {sampleSchema.map((t) => (
                <div key={t.table}>
                  <div className="flex items-baseline justify-between font-mono text-sm">
                    <span className="text-accent2">{t.table}</span>
                    {counts[t.table] !== undefined && (
                      <span className="text-[11px] text-muted">{counts[t.table].toLocaleString()} rows</span>
                    )}
                  </div>
                  <ul className="mt-1 font-mono text-xs text-muted space-y-0.5">
                    {t.columns.map((c) => (
                      <li key={c} className="pl-3">
                        {c}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
            <p className="mt-4 pt-3 border-t border-border text-[11px] leading-relaxed text-muted">
              The real pipeline handles 627 routes, 6,696 stops and 627K+ stop-times. This sandbox is a
              miniature of it.
            </p>
          </aside>
        </div>
      </div>
    </section>
  );
};
